import React, { Component } from "react";
import ChapterActionsMenu from "./ChapterActionsMenu"; // adjust path if needed

export default class ArchiveChaptersPanel extends Component {
  state = { open: false, query: "" };

  toggle = () => this.setState((s) => ({ open: !s.open }));
  onQueryChange = (e) => this.setState({ query: e.target.value || "" });
  onDragStart = (e, chapterId) => {
    e.dataTransfer.setData("text/plain", chapterId);
    e.dataTransfer.effectAllowed = "move";
  };
  onDraftDragOver = (e) => {
    if (this.props.sectionStatus !== "draft") return;
    e.preventDefault();
    if (!this.state.open) {
      this.setState({ open: true });
    }
  };
  onDraftDrop = async (e) => {
    if (this.props.sectionStatus !== "draft") return;
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    if (!draggedId) return;
    try {
      await this.props.onDropToDraft?.(draggedId);
    } catch (err) {
      alert(`Move to draft failed: ${err?.message || err}`);
    }
  };

  render() {
    const {
      project,
      chapters = [],
      loading,
      error,
      title = "Archive",
      emptyText = "No chapters",
      sectionStatus,
      searchable = false,
      onSetChapterStatus,
      onDeleteChapter,
      onOpenChapter,
    } = this.props;
    const { open, query } = this.state;

    

    if (!project) return null;

    const needle = query.trim().toLowerCase();
    const filtered = !needle
      ? chapters
      : chapters.filter((c) => {
          const titleText = String(c.title || "").toLowerCase();
          const contentText = String(c.content || "").toLowerCase();
          return titleText.includes(needle) || contentText.includes(needle);
        });

    // Sort: chapters with project_id first (grouped by project_id), then orphans (project_id null)
    const sorted = [...filtered].sort((a, b) => {
      const aHas = !!a.project_id;
      const bHas = !!b.project_id;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      // both have project or both orphan
      if (aHas && bHas) {
        if (a.project_id < b.project_id) return -1;
        if (a.project_id > b.project_id) return 1;
      }
      // tie-breakers: newer transition first, then title
      const aTime = new Date(a.updated_at || a.archived_at || a.created_at).getTime() || 0;
      const bTime = new Date(b.updated_at || b.archived_at || b.created_at).getTime() || 0;
      if (aTime !== bTime) return bTime - aTime;
      return String(a.title).localeCompare(String(b.title));
    });

    return (
      <div
        className="archive-panel"
        style={{ marginTop: 16 }}
        onDragOver={this.onDraftDragOver}
        onDrop={this.onDraftDrop}
      >
        {/* Header */}
        <div
          className="archive-header"
          onClick={this.toggle}
          style={{
            cursor: "pointer",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{open ? "▼" : "▶"} {title} ({chapters.length})</span>
          <span></span>
        </div>

        {/* Collapsible list */}
  {open && (
          <>
            {searchable && (
              <div style={{ marginTop: 8, marginBottom: 6 }}>
                <input
                  type="text"
                  value={query}
                  onChange={this.onQueryChange}
                  placeholder={`Search ${title.toLowerCase()}...`}
                  className="input"
                  style={{ width: "100%" }}
                />
              </div>
            )}
          <ul className="chapters-list" style={{ marginTop: searchable ? 0 : 8 }}>
            {loading && (
              <li className="chapter-row is-skeleton">
                <div className="chapter-main">
                  <div className="chapter-title">Loading…</div>
                </div>
              </li>
            )}
            {error && !loading && (
              <li className="chapter-row is-static">
                <div className="chapter-main">
                  <div className="chapter-title">{error}</div>
                </div>
              </li>
            )}
            {!loading && !error && sorted.length === 0 && (
              <li className="chapter-row is-static">
                <div className="chapter-main">
                  <div className="chapter-title">{needle ? `No matches in ${title.toLowerCase()}` : emptyText}</div>
                </div>
              </li>
            )}
            {!loading &&
              !error &&
              sorted.map((c, idx) => (
                <div key={c.id} className="chapter-row-wrap" style={{ position: "relative" }}>
                  <li
                    className={`chapter-row${sectionStatus === "archived" ? " is-locked" : ""}`}
                    draggable={sectionStatus === "draft"}
                    onDragStart={(e) => this.onDragStart(e, c.id)}
                    onClick={() => {
                      if (sectionStatus === "draft") {
                        onOpenChapter?.(c);
                      }
                    }}
                    title={sectionStatus === "archived" ? "Archived" : "Drag into an act to activate"}
                    style={{ cursor: sectionStatus === "draft" ? "pointer" : "default" }}
                  >
                    <div className="chapter-index">{c.chapter_number}</div>
                    <div className="chapter-main">
                      <div className="chapter-title">{c.title}</div>
                      <div className="chapter-meta">
                        {sectionStatus === "archived" ? "Archived" : "Draft"}{" "}
                        {new Date(c.updated_at || c.archived_at || c.created_at).toLocaleDateString()}
                        { !c.project_id && (
                          <span title="Orphaned chapter" style={{ marginLeft: 8, color: 'var(--text-2)' }}>
                            • Orphan
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                  <div
                    className="row-kebab-anchor"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: 8,
                      zIndex: 2,
                    }}
                  >
                    <ChapterActionsMenu
                      items={[
                        ...(sectionStatus === "archived"
                          ? [{
                              key: "restore",
                              label: "🔄 Restore to Active",
                              onClick: () => onSetChapterStatus?.(c.id, "active"),
                            }]
                          : [{
                              key: "active",
                              label: "✅ Move to Active",
                              onClick: () => onSetChapterStatus?.(c.id, "active"),
                            }, {
                              key: "archive",
                              label: "🗄 Archive",
                              onClick: () => onSetChapterStatus?.(c.id, "archived"),
                            }]),
                        {
                          key: "delete",
                          label: "🗑 Delete",
                          danger: true,
                          onClick: () => onDeleteChapter?.(c.id, c.title),
                        },
                      ]}
                      chapter={c}
                      readOnly={false}
                    />
                  </div>
                </div>
              ))}
          </ul>
          </>
        )}
      </div>
    );
  }
}
